import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que Recopilamos',
      text: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados de forma segura por nuestro proveedor de pagos.'
    }, {
      heading: '2. Uso de la Información',
      text: 'Tu información personal se utiliza exclusivamente para: entregar el producto digital adquirido, enviar confirmaciones de compra y comunicaciones relacionadas con tu pedido, y mejorar la experiencia del usuario en nuestro sitio.'
    }, {
      heading: '3. Protección de Datos',
      text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
    }, {
      heading: '4. Cookies',
      text: 'Nuestro sitio puede utilizar cookies esenciales para el funcionamiento del sitio y cookies analíticas para comprender cómo los visitantes interactúan con la página. Puedes desactivar las cookies en la configuración de tu navegador.'
    }, {
      heading: '5. Terceros',
      text: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros, excepto a proveedores de servicios de confianza que nos asisten en la operación del sitio y el procesamiento de pagos.'
    }, {
      heading: '6. Tus Derechos',
      text: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales disponibles en nuestro sitio.'
    }]
  } : {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Producto Digital',
      text: 'Al realizar la compra de "Operador de la Lattice: Código Silente", adquieres una licencia personal e intransferible para acceder y utilizar el contenido digital del ebook. Este producto es para uso personal únicamente.'
    }, {
      heading: '2. Entrega',
      text: 'El producto digital se entrega de forma inmediata tras la confirmación del pago. Recibirás un enlace de descarga en el correo electrónico proporcionado durante la compra.'
    }, {
      heading: '3. Política de Reembolso',
      text: 'Debido a la naturaleza digital e inmediata del producto, no se ofrecen reembolsos una vez completada la descarga. Si experimentas problemas técnicos con la descarga, nos comprometemos a resolver el inconveniente.'
    }, {
      heading: '4. Propiedad Intelectual',
      text: 'Todo el contenido del ebook, incluyendo texto, gráficos y diseño, está protegido por derechos de autor. Queda prohibida la reproducción, distribución o reventa total o parcial del contenido sin autorización expresa.'
    }, {
      heading: '5. Descargo de Responsabilidad',
      text: 'El contenido de este producto es de carácter educativo e informativo. No sustituye el consejo médico, psicológico o profesional. Los resultados pueden variar según cada individuo. El usuario asume la responsabilidad total del uso que haga de la información proporcionada.'
    }, {
      heading: '6. Modificaciones',
      text: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio.'
    }]
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-27">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} className="relative w-full max-w-2xl max-h-[80vh] bg-navy-900 border border-gold-500/20 rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-28">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 bg-navy-900/95 backdrop-blur-md" data-id="element-29">
          <h3 className="text-xl sm:text-2xl font-serif font-bold text-gold-400" data-id="element-30">
            {content.title}
          </h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors" aria-label="Cerrar" data-id="element-31">
            <XIcon className="w-5 h-5" data-id="element-32" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 sm:px-8 py-6 overflow-y-auto max-h-[calc(80vh-72px)] space-y-6" data-id="element-33">
          {content.sections.map((section, index) => <div key={index} data-id="element-34">
              <h4 className="text-lg font-semibold text-white mb-2" data-id="element-35">
                {section.heading}
              </h4>
              <p className="text-gray-400 leading-relaxed" data-id="element-36">{section.text}</p>
            </div>)}
          <p className="text-sm text-gray-600 pt-4 border-t border-white/5" data-id="element-37">
            Última actualización: Abril 2026
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 px-4 border-t border-white/5 bg-navy-950 text-center" data-id="element-38">
        <div className="max-w-4xl mx-auto" data-id="element-39">
          <p className="text-gold-500/50 font-serif text-xl mb-4" data-id="element-40">
            Código Silente
          </p>
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-41">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-gray-500 hover:text-gold-400 transition-colors underline underline-offset-4 decoration-gray-700 hover:decoration-gold-500/50" data-id="element-42">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-43">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-gray-500 hover:text-gold-400 transition-colors underline underline-offset-4 decoration-gray-700 hover:decoration-gold-500/50" data-id="element-44">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-gray-600 text-sm" data-id="element-45">
            &copy; {new Date().getFullYear()} Código Silente. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-46">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-47" />}
      </AnimatePresence>
    </>;
}